// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// mui
import {
	alpha,
	Box,
	Button,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography,
} from '@mui/material';

// icons
import {
	Add as IconAdd,
	AttachFile as IconAttachment,
	ContentCopy as IconCopy,
	Description as IconContract,
	Download as IconDownload,
	FolderOpen as IconComponent,
	GppGood as IconGate,
	List as IconList,
	RemoveCircleOutline as IconRemove,
	Settings as IconConfigure,
	Task as IconTask,
	UploadFile as IconUpload,
} from '@mui/icons-material';

// components

// v2 components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { ConditionsDataTable, ConditionRow } from './ConditionsDataTable';
import { StatCardRow } from '../../../ui/StatCardRow/StatCardRow';

// data
import { COLORS, GRADIENTS } from '../../../../data';

// ui
import { BtnSplit } from '../../../ui/BtnSplit/BtnSplit';

// types
import { PlanningActivity, Condition, WorkPlan } from '../../../../@types';

// dialogs
import {
	DialogBulkUpload,
	v2ConditionSchema,
	downloadTemplate,
	schemaWithCustomFields,
	LookupData,
} from '../../../dialogs/DialogBulkUpload';

// utils
import { getContractNum } from '../Contracts/Contracts.utils';

// hooks
import { useProjectLookups } from '../../../../hooks/useProjectLookups';

// config
import { featureFlags } from '../../../../config/featureFlags';

// condition tabs
import { ConfigureConditions } from './ConfigureConditions';

// HIERARCHICAL CONDITIONS MODEL

// Conditions are PROOF REQUIREMENTS (verification gates), not payment amounts.
// They follow a hierarchical inheritance pattern:
//
// Work Plan (e.g., "Civil Works")
//   └── Conditions: "Environmental Clearance", "Site Inspection"
//       │   (These apply to ALL activities within this work plan)
//       │
//       └── Activity (Commitment) (e.g., "Pump Station Construction")
//           └── Conditions: "Permits Obtained", "Safety Audit Complete"
//               │   (These apply to this specific activity)
//               │
//               └── Sub-Activity (Sub-Commitment) - future support
//                   └── Conditions: (inherits from parent + own conditions)
//
// Payment schedules are defined in CONTRACTS, not conditions.
// Contracts specify WHEN and HOW MUCH to pay (e.g., 30% on milestone 1).

// Types for hierarchical condition structure
interface ConditionUI {
	id: number;
	name: string;
	description: string | null;
	isRequired: boolean;
	isMet: boolean;
	status: string;
}

interface ActivityHierarchy {
	id: number;
	name: string;
	description: string | null;
	status: string;
	inheritedConditions: ConditionUI[];
	ownConditions: ConditionUI[];
	subActivities: {
		id: number;
		name: string;
		description: string | null;
		status: string;
		inheritedConditions: ConditionUI[];
		ownConditions: ConditionUI[];
	}[];
}

interface WorkPlanHierarchy {
	id: number;
	name: string;
	description: string | null;
	conditions: ConditionUI[];
	conditionsMet: number;
	conditionsTotal: number;
	activities: ActivityHierarchy[];
}

// Helper to convert V2ConditionSanitized to ConditionUI
const toConditionUI = (condition: Condition): ConditionUI => ({
	id: condition.id,
	name: condition.name,
	description: condition.description,
	isRequired: condition.isRequired,
	isMet: condition.status === 'APPROVED' || condition.status === 'VERIFIED',
	status: condition.status,
});

// Helper to get work plan ID from activity
const getWorkPlanIdFromActivity = (activity: PlanningActivity): number | null => {
	if (typeof activity.workPlan === 'number') return activity.workPlan;
	if (activity.workPlan && typeof activity.workPlan === 'object') return activity.workPlan.id;
	return null;
};

// Helper to get work plan ID from condition
const getWorkPlanIdFromCondition = (condition: Condition): number | null => {
	if (typeof condition.workPlan === 'number') return condition.workPlan;
	if (condition.workPlan && typeof condition.workPlan === 'object') return condition.workPlan.id;
	return null;
};

// Build hierarchical structure for conditions
const buildConditionHierarchy = (
	workPlans: WorkPlan[],
	activities: PlanningActivity[],
	conditions: Condition[]
): WorkPlanHierarchy[] => {
	return workPlans.map((workPlan) => {
		// Get activities for this work plan
		const workPlanActivities = activities.filter(
			(a) => getWorkPlanIdFromActivity(a) === workPlan.id
		);

		// Get conditions for this work plan
		const workPlanConditions = conditions
			.filter((c) => getWorkPlanIdFromCondition(c) === workPlan.id)
			.map(toConditionUI);

		return {
			id: workPlan.id,
			name: workPlan.name,
			description: workPlan.description || null,
			conditions: workPlanConditions,
			conditionsMet: workPlanConditions.filter((c) => c.isMet).length,
			conditionsTotal: workPlanConditions.length,
			activities: workPlanActivities.map((activity) => {
				// Get conditions for this activity (if linked via activity field)
				const activityConditions = conditions
					.filter((c) => {
						const actId = typeof c.activity === 'number' ? c.activity : c.activity?.id;
						return actId === activity.id;
					})
					.map(toConditionUI);

				// Get sub-activities
				const subActivities = (activity.children || []).map((subActivity) => ({
					id: subActivity.id,
					name: subActivity.name,
					description: subActivity.description || null,
					status: subActivity.status,
					inheritedConditions: workPlanConditions,
					ownConditions: [] as ConditionUI[],
				}));

				return {
					id: activity.id,
					name: activity.name,
					description: activity.description || null,
					status: activity.status,
					inheritedConditions: workPlanConditions,
					ownConditions: activityConditions,
					subActivities,
				};
			}),
		};
	});
};

export const Conditions = () => {
	// hooks
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const {
		activities,
		conditions,
		contracts,
		workPlans,
		loading,
		pagination,
		setFilterQuery,
		refetchConditions,
	} = useContext(ContextSpendPlan);

	// params
	const filterPage = searchParams.get('page') || '1';
	const filterLimit = searchParams.get('limit') || '10';
	const searchQuery = searchParams.get('search') || '';
	const filterComponent = searchParams.get('filterComponent') || 'ALL';
	const filterContract = searchParams.get('filterContract') || 'ALL';
	const filterStatusValue = searchParams.get('filterStatus') || 'ALL';
	const filterOrderBy = searchParams.get('orderBy') || '';
	const filterOrder = (searchParams.get('order') || 'DESC') as 'ASC' | 'DESC';

	// Sync URL params with context filter query
	useEffect(() => {
		setFilterQuery('conditions', {
			...(searchQuery && { search: searchQuery }),
			...(filterOrderBy && { orderBy: filterOrderBy }),
			...(filterComponent !== 'ALL' && { workPlan: parseInt(filterComponent) }),
			...(filterContract !== 'ALL' && { contract: parseInt(filterContract) }),
			...(filterStatusValue !== 'ALL' && { status: filterStatusValue }),
			order: filterOrder,
			page: parseInt(filterPage),
			limit: parseInt(filterLimit),
		});
	}, [
		filterPage,
		filterLimit,
		filterOrderBy,
		filterOrder,
		searchQuery,
		filterComponent,
		filterContract,
		filterStatusValue,
		setFilterQuery,
	]);

	// state
	const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);

	// Lookup data for bulk upload, fetched as the full project universe (no page
	// limit) so the resolve-relationship dropdowns list every work plan,
	// activity and contract for the project, not just the current listing page.
	const lookups = useProjectLookups({
		projectId: project?.id,
		isEnabled: bulkUploadDialogOpen,
		workPlans: true,
		activities: true,
		contracts: true,
	});
	const lookupData: LookupData = useMemo(
		() => ({
			workPlans: lookups.workPlans,
			activities: lookups.activities,
			contracts: lookups.contracts,
		}),
		[lookups.workPlans, lookups.activities, lookups.contracts]
	);
	// Tab state (URL-driven)
	const activeTab = searchParams.get('tab') === 'configure' ? 'configure' : 'all';
	const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
		setSearchParams((params) => {
			if (newValue === 'configure') {
				params.set('tab', 'configure');
			} else {
				params.delete('tab');
			}
			return params;
		});
	};

	const [quickAddForm, setQuickAddForm] = useState({
		component: '',
		name: '',
		description: '',
		paymentPercent: '',
	});

	// handlers for filters
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			params.set('page', '1');
			return params;
		});
	};

	const handleSortChange = (orderBy: string, order: 'ASC' | 'DESC') => {
		setSearchParams((params) => {
			params.set('orderBy', orderBy);
			params.set('order', order);
			return params;
		});
	};

	const handlePageChange = (page: number) => {
		setSearchParams((params) => {
			params.set('page', String(page));
			return params;
		});
	};

	const handleRowsPerPageChange = (rowsPerPage: number) => {
		setSearchParams((params) => {
			params.set('limit', String(rowsPerPage));
			params.set('page', '1');
			return params;
		});
	};

	const handleFilterChange = (key: string, value: string | number) => {
		setSearchParams((params) => {
			const paramKey = `filter${key.charAt(0).toUpperCase() + key.slice(1)}`;
			if (value && value !== 'ALL') {
				params.set(paramKey, String(value));
			} else {
				params.delete(paramKey);
			}
			params.set('page', '1');
			return params;
		});
	};

	// Build hierarchy from context data
	const hierarchy = useMemo(
		() => buildConditionHierarchy(workPlans, activities, conditions),
		[workPlans, activities, conditions]
	);

	// derived state

	// handlers
	const handleBulkUploadFile = () => {
		setBulkUploadDialogOpen(true);
	};

	const handleDownloadTemplate = async () => {
		try {
			await downloadTemplate({
				schema: schemaWithCustomFields(v2ConditionSchema, project?.customFieldDefinitions ?? []),
				lookupData,
			});
			setBanner({ message: 'Template downloaded', severity: 'success' });
		} catch {
			setBanner({ message: 'Failed to generate template', severity: 'error' });
		}
	};

	const handleClickCondition = (conditionId: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/condition/${conditionId}`);
	};

	const handleOpenAddConditionDialog = (context?: {
		activityId?: number;
		contractId?: number;
		workPlanId?: number;
	}) => {
		searchParams.set('dialog', 'addCondition');
		if (context?.workPlanId) {
			searchParams.set('dialogContextId', String(context.workPlanId));
			searchParams.set('dialogContextType', 'workPlan');
		} else if (context?.activityId) {
			searchParams.set('dialogContextId', String(context.activityId));
			searchParams.set('dialogContextType', 'activity');
		}
		setSearchParams(searchParams);
	};

	// Filter out TEMPLATE conditions — those belong in the Configure tab only
	const milestoneConditions = useMemo(
		() => conditions.filter((c) => c.type !== 'TEMPLATE'),
		[conditions]
	);

	// Calculate summary stats from hierarchy - Updated for mockup
	const summaryStats = useMemo(() => {
		// Get unique work plans (components) from conditions
		const uniqueWorkPlanIds = new Set<number>();
		milestoneConditions.forEach((c) => {
			const wpId = typeof c.workPlan === 'number' ? c.workPlan : c.workPlan?.id;
			if (wpId) uniqueWorkPlanIds.add(wpId);
		});

		// Get unique contracts with conditions
		const uniqueContractIds = new Set<number>();
		milestoneConditions.forEach((c) => {
			const contractId = typeof c.contract === 'number' ? c.contract : c.contract?.id;
			if (contractId) uniqueContractIds.add(contractId);
		});

		// Get unique activities with conditions
		const uniqueActivityIds = new Set<number>();
		milestoneConditions.forEach((c) => {
			const activityId = typeof c.activity === 'number' ? c.activity : c.activity?.id;
			if (activityId) uniqueActivityIds.add(activityId);
		});

		// Calculate total payment percentage earmarked
		const totalPaymentPercent = milestoneConditions.reduce(
			(sum, c) => sum + (c.paymentPercentage || 0),
			0
		);
		const avgPaymentPercent =
			milestoneConditions.length > 0 ? totalPaymentPercent / milestoneConditions.length : 0;

		// Total allocated amount (use paymentAmount as proxy)
		const totalAllocated = milestoneConditions.reduce((sum, c) => sum + (c.paymentAmount || 0), 0);

		return {
			componentsCount: uniqueWorkPlanIds.size || workPlans.length,
			contractsWithConditions: uniqueContractIds.size,
			activitiesWithConditions: uniqueActivityIds.size,
			totalPaymentEarmarked: avgPaymentPercent,
			totalAllocatedAmount: totalAllocated,
			workPlanConditions: hierarchy.reduce((sum, e) => sum + e.conditions.length, 0),
			workPlanConditionsMet: hierarchy.reduce((sum, e) => sum + e.conditionsMet, 0),
			workPlansWithUnmetConditions: hierarchy.filter((e) => e.conditionsMet < e.conditionsTotal)
				.length,
			totalWorkPlans: hierarchy.length,
			totalConditions: milestoneConditions.length,
			activeConditions: milestoneConditions.filter(
				(c) => c.status === 'VERIFIED' || c.status === 'APPROVED'
			).length,
			pendingConditions: milestoneConditions.filter(
				(c) => c.status === 'PENDING' || c.status === 'SUBMITTED'
			).length,
		};
	}, [hierarchy, milestoneConditions, workPlans]);

	// Flatten conditions for table view
	const flattenedConditions: ConditionRow[] = useMemo(() => {
		// When prerequisites flag is off, filter out base conditions
		const visibleConditions = featureFlags.PREREQUISITES
			? milestoneConditions
			: milestoneConditions.filter((c) => c.conditionType !== 'base');

		return visibleConditions.map((c) => {
			const workPlan =
				typeof c.workPlan === 'number' ? workPlans.find((wp) => wp.id === c.workPlan) : c.workPlan;
			const activity =
				typeof c.activity === 'number' ? activities.find((a) => a.id === c.activity) : c.activity;
			const contract =
				typeof c.contract === 'number' ? contracts.find((ct) => ct.id === c.contract) : c.contract;

			// Determine condition type based on conditionType field
			const isPaymentCondition = c.conditionType === 'payment';
			const level = isPaymentCondition ? t('spendPlan.conditions.paymentCondition') : t('spendPlan.conditions.baseCondition');
			// For payment conditions, show contract number; for base conditions, show work plan name
			let levelName = '';
			if (isPaymentCondition) {
				levelName = contract ? getContractNum(contract) || 'Contract' : 'Contract';
			} else {
				levelName = workPlan?.name || 'Work Plan';
			}

			return {
				...c,
				level,
				levelName,
				workPlanName: workPlan?.name,
				activityName: activity?.name,
				contractName: contract ? getContractNum(contract) : undefined,
			};
		});
	}, [milestoneConditions, workPlans, activities, contracts]);

	// Handle quick add form
	const handleQuickAddSubmit = () => {
		// TODO: Implement actual condition creation
		setBanner({
			message: `Condition "${quickAddForm.name}" added successfully (mockup)`,
			severity: 'success',
		});
		setQuickAddForm({ component: '', name: '', description: '', paymentPercent: '' });
	};

	return (
		<Box>
			{/* Header - Full width, no padding */}
			<HeaderListingPage
				icon={IconGate}
					title={t('spendPlan.conditions.title')}
					subtitle={t('spendPlan.conditions.subtitle')}
				variant="condition"
				actions={
					activeTab === 'all' ? (
						<BtnSplit
							actions={[
								{
									children: t('spendPlan.addCondition'),
									onClick: () => handleOpenAddConditionDialog(),
									startIcon: <IconAdd />,
								},
								{
									children: t('spendPlan.bulkUploadFromFile'),
									onClick: handleBulkUploadFile,
									startIcon: <IconUpload />,
								},
								{
									children: t('spendPlan.downloadTemplate'),
									onClick: handleDownloadTemplate,
									startIcon: <IconDownload />,
								},
							]}
						/>
					) : undefined
				}
			/>

			{/* Tab Menu */}
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					borderBottom: '1px solid',
					borderColor: 'divider',
					mb: 3,
				}}>
				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					variant="standard"
					sx={{ flex: 1, minHeight: 40 }}>
					<Tab
						icon={<IconList />}
						iconPosition="start"
						label={t('spendPlan.conditions.allConditions')}
						value="all"
						sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
					/>
					<Tab
						icon={<IconConfigure />}
						iconPosition="start"
						label={t('spendPlan.conditions.configureConditions')}
						value="configure"
						sx={{ textTransform: 'none', fontWeight: 600, minHeight: 40, py: 0.5 }}
					/>
				</Tabs>
			</Box>

			{/* Tab: All Conditions */}
			{activeTab === 'all' && (
				<Box sx={{ pb: 3, px: 3, pt: 2 }}>
					{/* Summary Cards */}
					<StatCardRow
						loading={loading.conditions}
						cards={[
							{
								icon: IconComponent,
								label: t('components'),
								value: summaryStats.componentsCount,
								secondary: t('spendPlan.conditions.workPlansCount', { count: summaryStats.totalWorkPlans }),
								color: GRADIENTS.workPlan.icon,
							},
							{
								icon: IconContract,
								label: t('contracts'),
								value: summaryStats.contractsWithConditions,
								color: GRADIENTS.contract.icon,
							},
							{
								icon: IconTask,
								label: t('activities'),
								value: summaryStats.activitiesWithConditions,
								color: GRADIENTS.activity.icon,
							},
							{
								icon: IconGate,
								label: t('spendPlan.conditions.paymentEarmarked'),
								value: `${summaryStats.totalPaymentEarmarked || 0}%`,
								secondary: t('spendPlan.conditions.conditionsCount', { count: summaryStats.totalConditions }),
								color: COLORS.success.main,
							},
						]}
					/>

					{/* Conditions DataTable */}
					<ConditionsDataTable
						data={flattenedConditions}
						isLoading={loading.conditions}
						searchValue={searchQuery}
						onSearchChange={handleSearchChange}
						orderBy={filterOrderBy}
						order={filterOrder}
						onSortChange={handleSortChange}
						page={parseInt(filterPage)}
						rowsPerPage={parseInt(filterLimit)}
						totalCount={pagination.conditions?.total || flattenedConditions.length}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handleRowsPerPageChange}
						filterValues={{
							component: filterComponent,
							contract: filterContract,
							status: filterStatusValue,
						}}
						onFilterChange={handleFilterChange}
						onRowClick={(condition) => handleClickCondition(condition.id)}
						onEditCondition={(condition) => {
							navigate(`/project/${project?.idWbg}/spend-plans/condition/${condition.id}`);
						}}
						onLinkProcurement={() => {
							setBanner({
								message: 'Link to Procurement feature coming soon',
								severity: 'info',
							});
						}}
						onArchiveCondition={() => {
							setBanner({
								message: t('spendPlan.conditions.archived'),
								severity: 'info',
							});
						}}
						onCreateWithAI={() => {
							setBanner({
								message:
									'AI condition generation is available when creating a milestone. Go to a contract, add a milestone, and use "Generate Conditions with AI" in the Payment Terms & Conditions step.',
								severity: 'info',
							});
						}}
						footer={
							<Box
								sx={{
									bgcolor: alpha(GRADIENTS.condition.icon, 0.03),
									p: 2,
								}}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Typography
										variant="body2"
										sx={{
											color: GRADIENTS.condition.icon,
											fontWeight: 600,
											minWidth: 120,
										}}>
									{t('spendPlan.conditions.quickAdd')}
									</Typography>
									<FormControl size="small" sx={{ minWidth: 150 }}>
									<InputLabel>{t('component')}</InputLabel>
									<Select
										value={quickAddForm.component}
										label={t('component')}
											onChange={(e) =>
												setQuickAddForm((prev) => ({ ...prev, component: e.target.value }))
											}>
											{workPlans.map((wp) => (
												<MenuItem key={wp.id} value={String(wp.id)}>
													{wp.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									<TextField
										size="small"
										placeholder={t('spendPlan.conditions.conditionName')}
										value={quickAddForm.name}
										onChange={(e) => setQuickAddForm((prev) => ({ ...prev, name: e.target.value }))}
										sx={{ minWidth: 180 }}
									/>
									<TextField
										size="small"
										placeholder={t('description')}
										value={quickAddForm.description}
										onChange={(e) =>
											setQuickAddForm((prev) => ({ ...prev, description: e.target.value }))
										}
										sx={{ flex: 1 }}
									/>
									<TextField
										size="small"
										placeholder={t('spendPlan.conditions.paymentPct')}
										type="number"
										value={quickAddForm.paymentPercent}
										onChange={(e) =>
											setQuickAddForm((prev) => ({ ...prev, paymentPercent: e.target.value }))
										}
										sx={{ width: 100 }}
										InputProps={{
											endAdornment: <Typography color="text.secondary">%</Typography>,
										}}
									/>
									<Stack direction="row" spacing={0.5}>
										<IconButton size="small" sx={{ color: 'text.secondary' }}>
											<IconAttachment fontSize="small" />
										</IconButton>
										<IconButton size="small" sx={{ color: 'text.secondary' }}>
											<IconCopy fontSize="small" />
										</IconButton>
										<IconButton size="small" sx={{ color: 'error.main' }}>
											<IconRemove fontSize="small" />
										</IconButton>
									</Stack>
									<Button
										variant="contained"
										size="small"
										disabled={!quickAddForm.name || !quickAddForm.component}
										onClick={handleQuickAddSubmit}
										sx={{ minWidth: 80 }}>
											{t('add')}
									</Button>
								</Stack>
							</Box>
						}
					/>

					{/* Bulk Upload Conditions Dialog */}
					<DialogBulkUpload
						isOpen={bulkUploadDialogOpen}
						onClose={() => setBulkUploadDialogOpen(false)}
						schema={v2ConditionSchema}
						projectId={project?.id || 0}
						lookupData={lookupData}
						customFieldDefinitions={project?.customFieldDefinitions}
						onSuccess={() => {
							refetchConditions();
							setBanner({ message: t('spendPlan.conditions.uploadedSuccess'), severity: 'success' });
						}}
					/>
				</Box>
			)}

			{/* Tab: Configure Conditions */}
			{activeTab === 'configure' && (
				<Box sx={{ pb: 3, px: 3, pt: 2 }}>
					<ConfigureConditions />
				</Box>
			)}
		</Box>
	);
};

export default Conditions;
